import { useState, useCallback } from "react";
import api, { authAPI } from "../../services/api";
import { User } from "../types/admin.types";

export interface UseUsersReturn {
  users: User[];
  loadUsers: () => Promise<void>;
  handleAddUser: (formData: {
    id: string;
    email: string;
    password: string;
    full_name: string;
    role: string;
  }) => Promise<void>;
  handleEditUser: (
    editingUser: User,
    formData: {
      id: string;
      email: string;
      password: string;
      full_name: string;
      role: string;
    }
  ) => Promise<void>;
  handleDeleteUser: (userId: number) => Promise<void>;
}

export const useUsers = (
  setError: (msg: string) => void,
  setSuccess: (msg: string) => void,
  setLoading: (v: boolean) => void
): UseUsersReturn => {
  const [users, setUsers] = useState<User[]>([]);

  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data.users);
    } catch {
      setError("Failed to load users");
    }
  }, [setError]);

  const handleAddUser = async (formData: {
    id: string; email: string; password: string; full_name: string; role: string;
  }) => {
    setLoading(true);
    try {
      const userData: any = {
        id: parseInt(formData.id),
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role,
      };
      if (formData.email?.trim()) userData.email = formData.email;
      await authAPI.register(userData);
      setSuccess("User added successfully!");
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to add user");
      throw err; // let caller close modal conditionally
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (
    editingUser: User,
    formData: { id: string; email: string; password: string; full_name: string; role: string }
  ) => {
    setLoading(true);
    try {
      const updates: any = {};
      if (formData.id && formData.id !== editingUser.id.toString()) updates.id = parseInt(formData.id);
      if (formData.email) updates.email = formData.email;
      if (formData.password) updates.password = formData.password;
      if (formData.full_name) updates.full_name = formData.full_name;
      if (formData.role) updates.role = formData.role;
      await api.put(`/users/${editingUser.id}`, updates);
      setSuccess("User updated successfully!");
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update user");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm("Are you sure you want to delete this user? This will also delete all their payslips.")) return;
    try {
      await api.delete(`/users/${userId}`);
      setSuccess("User deleted successfully");
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete user");
    }
  };

  return { users, loadUsers, handleAddUser, handleEditUser, handleDeleteUser };
};
